/**
 * 稿紙四角偵測 API 用戶端(kanban 114186:四角偵測改由後端推論)。
 *
 * 取代原本瀏覽器內 ONNX 推論(cornerDetector.ts,已移除)。後端契約:
 *   POST {VITE_API_URL}/cornerdetect
 *   body { image: "data:image/jpeg;base64,...", output: "warped" | "corners" }
 *   200 成功: { corners: [[x,y]*4], width, height, warpedImage: "data:image/jpeg;base64,..." }
 *   200 偵測不到(fail-open): { corners: null, reason: "..." }(無 warpedImage 欄位)
 *   422 請求本身有問題: { detail }
 *   503 忙碌中(排隊逾時)或模型未就緒: { detail } —— 這個要重試
 *
 * baseURL / authToken 沿用 services/api.ts 的 getApiConfig(),與既有其他功能同一組
 * VITE_API_URL / VITE_API_AUTH_TOKEN,不另外發明新的 env 命名。用輕量 fetch(不走
 * services/api.ts 的 axios 實例):那個實例的 timeout 是給批改用的 1800 秒與整批
 * 攔截器記錄,不適合這裡「每次請求 10 秒 + 503 重試」的節奏,fetch 也讓測試能直接
 * stub 全域 fetch(這個 repo既有慣例,見本檔的 cornerDetectApi.test.ts)。
 *
 * 呼叫端:UploadEssay.tsx 舊手動校正視窗的自動框選預填(initializePerspectivePoints)。
 */

import { getApiConfig } from "../services/api";

/** 角點座標,[x, y]。順序與本地舊版 detectCorners 慣例一致:TL, TR, BR, BL。 */
export type CornerDetectPoint = [number, number];

export interface CornerDetectSuccess {
  corners: CornerDetectPoint[];
  width: number;
  height: number;
  /** JPEG quality 90 的 data URL。只有偵測到四角(corners 非 null)時才有這個欄位。 */
  warpedImage: string;
}

/** 偵測不到稿紙區域(fail-open):200 回應但沒有 warpedImage,呼叫端要視同偵測失敗。 */
export interface CornerDetectMiss {
  corners: null;
  reason: string;
}

export type CornerDetectResult = CornerDetectSuccess | CornerDetectMiss;

export interface CornerDetectOptions {
  /** 高中固定用 warped:直接拿校正後圖片,不用自己再做一次透視變形。 */
  output?: "warped" | "corners";
  /** 單次請求的逾時毫秒數(不含重試等待)。 */
  requestTimeoutMs?: number;
  /** 503 最多重試次數(不含第一次請求)。 */
  maxRetries?: number;
  /** 503 重試的退避毫秒數,依 attempt 索引取值,超出陣列長度時沿用最後一個值。 */
  retryDelaysMs?: number[];
  /** 只給測試用:注入假的 fetch。 */
  fetchImpl?: typeof fetch;
  /** 只給測試用:注入假的等待函式,跳過重試退避的真實等待。 */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  /**
   * 外部中止訊號(kanban 114186 code review)。設計給呼叫端(目前是 UploadEssay.tsx
   * 舊手動校正視窗的自動框選預填)自己包一層外層逾時 race,resolve 後
   * 用這個真的 abort 掉還在進行的請求與重試迴圈,不讓它繼續在背景空打一個已經被判定
   * 逾時的後端。目前呼叫端還沒接上這個 signal(逾時控管在 fetch 層的
   * requestTimeoutMs 就夠用),先留著給未來需要外層逾時的呼叫端用。abort 時:
   * - 進行中的請求會被中止(轉發給該次請求自己的 AbortController)
   * - 重試迴圈不會再送下一次請求(下一輪開始前先檢查)
   */
  signal?: AbortSignal;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 10000;
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAYS_MS = [1000, 2000, 4000];

const defaultSleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });

/**
 * 呼叫後端稿紙四角偵測 API。
 *
 * fail-open 契約 —— 除了「單次 503」會依 retryDelaysMs 重試外,任何失敗都回傳 null,
 * 這支本身絕不 throw,由呼叫端(UploadEssay.tsx 舊手動校正視窗的自動框選預填)
 * 決定送未校正的原圖:
 * - 逾時(AbortError)/網路中斷 → null,不重試(逾時預算已包含在 requestTimeoutMs 裡,
 *   重試的是「伺服器忙碌」不是「網路不穩」,語意不同)
 * - 422(請求本身有問題)→ null,不重試(重送同一個壞請求沒有意義)
 * - 503(忙碌中/模型未就緒)→ 依 retryDelaysMs 重試,重試耗盡回 null
 * - 其他非 2xx → null,不重試
 * - 200 但 corners: null(偵測不到)→ 原樣回傳給呼叫端,由呼叫端視為 fail-open
 *
 * options.signal 由外部(呼叫端的逾時 race)abort 時:每一輪迴圈開始前先檢查,
 * 已中止就不再送下一次請求;進行中的請求則轉發到該次請求自己的 AbortController,
 * 讓 fetch 真的被中止,不會繼續在背景空打一個外層已經判定逾時的後端。
 */
export async function requestCornerDetect(
  imageDataUrl: string,
  options: CornerDetectOptions = {}
): Promise<CornerDetectResult | null> {
  const {
    output = "warped",
    requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    maxRetries = DEFAULT_MAX_RETRIES,
    retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
    fetchImpl = fetch,
    sleep = defaultSleep,
    signal,
  } = options;
  const { baseURL, authToken } = getApiConfig();
  const url = `${baseURL}/cornerdetect`;

  for (let attempt = 0; ; attempt++) {
    if (signal?.aborted) return null;

    const controller = new AbortController();
    const onExternalAbort = () => controller.abort();
    if (signal) signal.addEventListener("abort", onExternalAbort, { once: true });
    const timer = setTimeout(() => controller.abort(), requestTimeoutMs);
    try {
      const resp = await fetchImpl(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ image: imageDataUrl, output }),
        signal: controller.signal,
      });

      if (resp.status === 503 && attempt < maxRetries) {
        if (signal?.aborted) return null;
        const delay = retryDelaysMs[Math.min(attempt, retryDelaysMs.length - 1)];
        await sleep(delay, signal);
        continue;
      }
      if (!resp.ok) return null;
      return (await resp.json()) as CornerDetectResult;
    } catch {
      // AbortError(逾時,含外部 signal 中止)或網路層錯誤 → 不重試,直接 fail-open
      return null;
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener("abort", onExternalAbort);
    }
  }
}
